<template>
  <v-dialog
    v-model="isOpen"
    max-width="600"
    persistent
  >
    <v-card
      class="ns-card generate-dialog"
      :elevation="0"
    >
      <div class="dialog-header">
        <span class="dialog-title">Generate Resources</span>
        <v-btn
          class="ns-btn-secondary"
          icon
          :elevation="0"
          size="small"
          @click="close"
        >
          <font-awesome-icon icon="xmark" />
        </v-btn>
      </div>

      <div class="dialog-body">
        <!-- Shared generation parameters -->
        <div class="section-label">
          Generation Settings
        </div>

        <v-text-field
          v-model="topic"
          label="Topic"
          variant="outlined"
          density="compact"
          hide-details="auto"
          class="form-field"
          placeholder="e.g. Morning routines"
        />

        <v-select
          v-model="linguisticLevel"
          :items="linguisticLevels"
          label="Linguistic Level"
          variant="outlined"
          density="compact"
          hide-details="auto"
          class="form-field"
        />

        <!-- Document list -->
        <div class="section-row">
          <div class="section-label">
            Documents
          </div>
          <v-btn
            class="ns-btn-secondary add-doc-btn"
            :elevation="0"
            size="small"
            :disabled="documents.length >= maxDocs"
            @click="addDocument"
          >
            <font-awesome-icon
              icon="plus"
              style="margin-right: 4px"
            />
            Add
          </v-btn>
        </div>

        <div
          v-for="(doc, index) in documents"
          :key="index"
          class="document-entry"
        >
          <div class="document-entry-header">
            <span class="document-index">Document {{ index + 1 }}</span>
            <v-btn
              icon
              variant="text"
              size="x-small"
              :elevation="0"
              @click="removeDocument(index)"
            >
              <font-awesome-icon icon="xmark" />
            </v-btn>
          </div>

          <v-select
            v-model="doc.document_type"
            :items="documentTypeOptions"
            item-title="title"
            item-value="value"
            label="Document Type"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="form-field"
          />

          <v-text-field
            v-model="doc.keywordsRaw"
            label="Keywords (comma-separated)"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="form-field"
            placeholder="e.g. culture, fun, daily life"
          />

          <v-text-field
            v-model="doc.other_considerations"
            label="Other Considerations"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="form-field"
            placeholder="e.g. Keep sentences short"
          />
        </div>

        <div
          v-if="documents.length === 0"
          class="empty-state"
        >
          Click "Add" to add a document to generate.
        </div>
      </div>

      <div class="dialog-footer">
        <v-btn
          class="ns-btn-secondary"
          :elevation="0"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          class="ns-btn-primary"
          :elevation="0"
          :loading="isLoading"
          :disabled="!isFormValid"
          @click="submit"
        >
          Generate
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { generateResources } from '@/services/knowledgebaseService'
import {
  GENERATE_RESOURCES_MAX_DOCS,
  GENERATE_RESOURCES_DOC_TYPES,
  GENERATE_RESOURCES_LINGUISTIC_LEVELS,
  GENERATE_RESOURCES_SUCCESS_MSG,
  GENERATE_RESOURCES_ERROR_MSG,
  DEFAULT_LANGUAGE,
  DOCUMENT_TYPE_DISPLAY_MAP,
  TOO_MANY_REQUESTS_STATUS,
  GENERATE_RESOURCES_MAX_DOCS_ERROR_MSG
} from '@/assets/constants/constants'

export default {
  name: 'GenerateResourcesDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    subjectId: {
      type: [String, Number],
      default: null
    }
  },
  emits: ['update:visible', 'show-toast'],
  data() {
    return {
      topic: '',
      language: DEFAULT_LANGUAGE,
      linguisticLevel: '',
      documents: [],
      isLoading: false,
      maxDocs: GENERATE_RESOURCES_MAX_DOCS,
      documentTypes: GENERATE_RESOURCES_DOC_TYPES,
      linguisticLevels: GENERATE_RESOURCES_LINGUISTIC_LEVELS
    }
  },
  computed: {
    isOpen: {
      get() { return this.visible },
      set(value) { this.$emit('update:visible', value) }
    },
    isFormValid() {
      if (!this.topic.trim() || !this.language.trim() || !this.linguisticLevel) return false
      if (this.documents.length === 0) return false
      return this.documents.every(doc => doc.document_type)
    },
    documentTypeOptions() {
      return GENERATE_RESOURCES_DOC_TYPES.map(type => ({
        title: DOCUMENT_TYPE_DISPLAY_MAP[type],
        value: type
      }))
    }
  },
  methods: {
    addDocument() {
      if (this.documents.length >= this.maxDocs) return
      this.documents.push({
        document_type: '',
        keywordsRaw: '',
        other_considerations: ''
      })
    },
    removeDocument(index) {
      this.documents.splice(index, 1)
    },
    close() {
      this.resetForm()
      this.isOpen = false
    },
    resetForm() {
      this.topic = ''
      this.language = ''
      this.linguisticLevel = ''
      this.documents = []
      this.isLoading = false
    },
    async submit() {
      if (!this.isFormValid) return
      this.isLoading = true
      try {
        const payload = {
          subject_id: this.subjectId,
          topic: this.topic.trim(),
          language: this.language.trim(),
          linguistic_level: this.linguisticLevel,
          documents: this.documents.map(doc => ({
            document_type: doc.document_type,
            keywords: doc.keywordsRaw
              .split(',')
              .map(k => k.trim())
              .filter(k => k.length > 0),
            other_considerations: doc.other_considerations.trim()
          }))
        }
        const response = await generateResources(payload)
        if (response?.status === TOO_MANY_REQUESTS_STATUS) {
          const error = new Error(GENERATE_RESOURCES_MAX_DOCS_ERROR_MSG)
          error.statusCode = TOO_MANY_REQUESTS_STATUS
          throw error
        }
        this.$emit('show-toast', { severity: 'success', summary: 'Success', detail: GENERATE_RESOURCES_SUCCESS_MSG, life: 5000 })
        this.close()
      } catch (error) {
        const detail = error.status === TOO_MANY_REQUESTS_STATUS 
          ? GENERATE_RESOURCES_MAX_DOCS_ERROR_MSG 
          : GENERATE_RESOURCES_ERROR_MSG
        this.$emit('show-toast', { severity: 'error', summary: 'Error', detail, life: 5000 })
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
.generate-dialog {
  padding: var(--space-4);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-4);
}

.dialog-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.dialog-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  max-height: 60vh;
  overflow-y: auto;
  padding-right: var(--space-1);
}

.section-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: var(--space-2);
}

.section-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin-top: var(--space-2);
}

.form-field {
  margin-bottom: var(--space-1);
}

.document-entry {
  border: 1px solid rgba(27, 30, 100, 0.10);
  border-radius: 6px;
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  background: var(--color-surface);
}

.document-entry-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-1);
}

.document-index {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-secondary);
}

.add-doc-btn {
  min-width: unset;
}

.empty-state {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-align: center;
  padding: var(--space-4) 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  margin-top: var(--space-4);
}
</style>
